import { useState, useRef, ReactElement } from 'react';
import { Popover as TinyPopover } from 'react-tiny-popover';
import { useEventListener } from 'usehooks-ts';
import { Transition } from '@headlessui/react';
import clsx from 'clsx';

/**
 * Display a popover (tooltip) when the child is hovered.
 */
export function Popover({
  children,
  content,
  className,
  closeOnScroll = true,
}: {
  children: ReactElement;
  /**
   * Content of the popover (tooltip).
   */
  content: string | ReactElement;
  className?: string;
  /**
   * Should the popover close on a scroll event
   */
  closeOnScroll?: boolean;
}) {
  const [isOpen, setIsOpen] = useState(false);
  const childRef = useRef(null);

  useEventListener('mouseover', () => setIsOpen(true), childRef);
  useEventListener('touchstart', () => setIsOpen(true), childRef);

  useEventListener('mouseout', () => setIsOpen(false), childRef);

  useEventListener('scroll', () => {
    if (closeOnScroll) setIsOpen(false);
  });

  // append to main layout to get font styles
  const parentEl =
    typeof document === 'undefined'
      ? undefined
      : (document.getElementById('__next')?.firstChild as HTMLElement);

  return (
    <>
      {/* z-index must be > than z-index set in BrowseFilters.tsx for mobile popover to display */}
      <TinyPopover
        isOpen={isOpen}
        parentElement={parentEl}
        containerClassName="z-[10000] md:z-5"
        content={
          <Transition
            show={isOpen}
            enter="transition ease-in duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="transition ease-out duration-300"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
            className={clsx(
              'z-[10000] max-w-xs rounded border border-gray-400 bg-white py-1 px-2 font-header text-sm shadow-lg md:z-5',
              className,
            )}
          >
            {content}
          </Transition>
        }
        ref={childRef}
      >
        {children}
      </TinyPopover>
    </>
  );
}
